import React, { ButtonHTMLAttributes } from 'react';
import styled from 'styled-components';

import useScreenQuery from '../../hooks/useScreenQuery';
import PlayupThemeProvider from '../../themes/PlayupThemeProvider';
import { getButtonBackgroundColor, getButtonBorderColor, getButtonTextColor } from '../../themes/themeSelector';

const Button = ({
	kind = 'blackslate',
	isLoading = false,
	contentSize,
	height,
	children,
	...HTMLbuttonProps
}: ButtonProps) => {
	const { isSmall } = useScreenQuery();

	return (
		<PlayupThemeProvider>
			<StyledButton kind={kind} isSmallScreen={isSmall} contentSize={contentSize} height={height} {...HTMLbuttonProps}>
				{isLoading ? (
					<Spinner kind={kind} isSmallScreen={isSmall} size={contentSize} />
				) : (
					<ButtonContent>{children}</ButtonContent>
				)}
			</StyledButton>
		</PlayupThemeProvider>
	);
};

export default Button;

export type buttonKinds =
	| 'blackslate'
	| 'white'
	| 'hollow-white'
	| 'hollow-black'
	| 'transparent-white'
	| 'danger'
	| 'primary'
	| 'fantasy'
	| 'playup';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	kind?: buttonKinds;
	isLoading?: boolean;
	contentSize?: number;
	height?: number;
}

const StyledButton = styled.button<{
	kind: buttonKinds;
	isSmallScreen: boolean;
	contentSize?: number;
	height?: number;
}>`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	height: ${({ isSmallScreen, height }) => (height === undefined ? (isSmallScreen ? 50 : 60) : height)}px;
	padding: 12px;
	border-radius: 5px;
	border: 1px solid ${({ theme, kind }) => getButtonBorderColor(theme, kind)};
	font-size: ${({ isSmallScreen, contentSize }) =>
		contentSize === undefined ? (isSmallScreen ? 16 : 20) : contentSize}px;
	font-weight: 600;
	transition: 200ms;

	color: ${({ theme, kind }) => getButtonTextColor(theme, kind)};
	background-color: ${({ theme, kind }) => getButtonBackgroundColor(theme, kind)};

	&:focus {
		outline: none;
	}

	&:hover:not([disabled]) {
		opacity: 0.7;
		color: ${({ theme, kind }) => getButtonTextColor(theme, kind)};
		background-color: ${({ theme, kind }) => getButtonBackgroundColor(theme, kind)};
	}

	&[disabled] {
		opacity: 0.5;
		cursor: default;
	}
`;

const ButtonContent = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	user-select: none;
`;

const Spinner = styled.div<{ kind: buttonKinds; isSmallScreen: boolean; size?: number }>`
	margin-right: ${({ theme }) => theme.spacings[1]};
	width: 1.5em;
	height: 1.5em;
	font-size: ${({ isSmallScreen, size }) => (size === undefined ? (isSmallScreen ? 16 : 20) : size)}px;
	position: relative;
	text-indent: -9999em;
	border-radius: 50%;
	border: 0.2em solid ${({ theme, kind }) => getButtonTextColor(theme, kind)};
	border-left-color: transparent;
	transform: translateZ(0);
	animation: spinning 1s infinite linear;

	@keyframes spinning {
		0% {
			transform: rotate(0deg);
		}

		100% {
			transform: rotate(360deg);
		}
	}
`;
